
import { LandingNav } from "@/components/landing/LandingNav";
import { SEO } from "@/components/SEO";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, BarChart2, Share2, Users, FileText, Settings, Shield, Zap, Globe } from "lucide-react";

const features = [
    {
        icon: Users,
        title: "Unlimited Entries",
        description: "On Starter plans and above, accept as many entries as you want. No caps, no surprise upgrade prompts."
    },
    {
        icon: Share2,
        title: "Viral sharing built-in",
        description: "Participants earn bonus entries for referring friends. Watch your contest grow organically."
    },
    {
        icon: FileText,
        title: "Custom Fields",
        description: "Collect exactly the data you need. Add custom text inputs, checkboxes, or dropdowns."
    },
    {
        icon: Settings,
        title: "Branding Control",
        description: "Remove RaffleKit branding and make the contest page look like your own with custom colors and logos."
    },
    {
        icon: BarChart2,
        title: "Export & Analytics",
        description: "Download all your participant data as CSV anytime. View real-time stats on traffic and conversions."
    },
    {
        icon: Zap,
        title: "Dead Simple Setup",
        description: "Launch in minutes. No coding required. Just fill in your prize details and go live."
    },
    {
        icon: Shield,
        title: "Fair Winner Selection",
        description: "Our system randomly selects winners from all valid entries. Fully transparent and fair."
    },
    {
        icon: Globe,
        title: "Embed Anywhere",
        description: "Run the contest on our hosted page or embed it directly on your own website with a simple code snippet."
    },
    {
        icon: Settings,
        title: "Integrations",
        description: "Sync data to your favorite CRMs and marketing tools."
    }
];

const Features = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Features — RaffleKit Giveaway Software"
                description="Viral referral tracking, customizable entry methods, fraud protection, and analytics. Everything you need to run a successful giveaway."
                canonical="https://rafflekit.app/features"
            />
            <LandingNav />

            {/* Hero Section */}
            <section className="pt-32 pb-20 bg-gray-50">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Everything you need to run a giveaway. <br className="hidden md:block" />
                        <span className="text-primary">Nothing you don't.</span>
                    </h1>
                    <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10">
                        Powerful features designed for small businesses. Simple setup, transparent pricing, and no enterprise bloat.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <Button asChild size="lg" variant="accent" className="font-bold text-lg h-12 px-8 rounded-full">
                            <Link to="/admin?mode=signup">
                                Start Free
                            </Link>
                        </Button>
                        <Button asChild variant="outline" size="lg" className="h-12 px-8 rounded-full bg-white">
                            <Link to="/how-it-works">
                                How It Works
                            </Link>
                        </Button>
                        <Button asChild variant="outline" size="lg" className="h-12 px-8 rounded-full bg-white hidden md:flex">
                            <Link to="/pricing">
                                View Pricing
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div key={index} className="p-6 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all group bg-white">
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <feature.icon className="w-6 h-6 text-primary" />
                                </div>
                                <h3 className="font-bold text-xl text-gray-900 mb-2 flex items-center gap-2">
                                    {feature.title}
                                    {feature.title === "Webhooks & API" && (
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                            Coming Soon
                                        </span>
                                    )}
                                </h3>
                                <p className="text-gray-500">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Built for Small Business */}
            <section className="py-20 bg-gray-50/50">
                <div className="container mx-auto px-4">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
                            Built specially for small businesses.
                        </h2>
                        <p className="text-gray-500 text-lg">
                            We stripped away the complexity so you can focus on growth.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 text-center max-w-5xl mx-auto">
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Check className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="font-bold text-xl text-gray-900 mb-2">Dead Simple Setup</h3>
                            <p className="text-gray-500">Launch in minutes, not days. No developer needed.</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Check className="w-8 h-8 text-blue-600" />
                            </div>
                            <h3 className="font-bold text-xl text-gray-900 mb-2">Transparent Pricing</h3>
                            <p className="text-gray-500">Know exactly what you pay. No hidden fees or limits.</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Check className="w-8 h-8 text-purple-600" />
                            </div>
                            <h3 className="font-bold text-xl text-gray-900 mb-2">No Enterprise Bloat</h3>
                            <p className="text-gray-500">Only the features you need to grow your audience.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Band */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">
                        Launch your first giveaway in minutes.
                    </h2>
                    <Button asChild size="lg" variant="accent" className="font-bold text-lg h-14 px-10 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
                        <Link to="/admin?mode=signup">
                            Start Free
                        </Link>
                    </Button>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default Features;
